// Phase 3 — cricket player career stats modal.
// Renders one player's profile + career stats scoped to a specific
// cricket team. Eight built-in fields (matches, runs, wickets, catches,
// maiden overs, 100's, 6's, 4's) plus any admin-defined custom fields
// pulled from sports.CricketStatFields. Leadership + captains can edit;
// everyone else gets a read-only view.

import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import {
  X, Trophy, Target, Hand, Shield, Save, Loader2,
  User as UserIcon, Award, Zap, Sigma, BarChart3
} from "lucide-react";
import { api } from "../api";
import { useToast } from "./Toast";
import { canDo } from "../data";

const EMPTY_STATS = {
  matchesPlayed: 0,
  runsScored: 0,
  wicketsTaken: 0,
  catchesTaken: 0,
  maidenOvers: 0,
  centuries: 0,
  sixes: 0,
  fours: 0
};

// Built-in fields render first, in this fixed order, with a fixed
// styling. Admin-defined custom fields render after them, ordered by
// the SortOrder column in the CricketStatFields table.
const BUILTIN_FIELDS = [
  { key: "matchesPlayed", label: "Matches Played",       icon: Trophy, accent: "var(--gold)" },
  { key: "runsScored",    label: "Runs Scored",          icon: Target, accent: "#5ce1e6" },
  { key: "wicketsTaken",  label: "Wickets Taken",        icon: Shield, accent: "#ff8a2a" },
  { key: "catchesTaken",  label: "Catches Taken",        icon: Hand,   accent: "#c897ff" },
  { key: "maidenOvers",   label: "Maiden Overs Bowled",  icon: Target, accent: "#ffd76a" },
  { key: "centuries",     label: "100's",                icon: Award,  accent: "#ffd76a" },
  { key: "sixes",         label: "6's",                  icon: Zap,    accent: "#5ce1e6" },
  { key: "fours",         label: "4's",                  icon: Sigma,  accent: "#c897ff" }
];

// Look up a lucide component by name with a safe fallback. Used for
// admin-defined custom fields whose icon is stored as a string.
function iconCmp(name) {
  return Icons[name] || BarChart3;
}

export default function PlayerProfile({ user, team, currentUser, onClose }) {
  const toast = useToast();
  const [stats, setStats] = useState(EMPTY_STATS);
  const [customValues, setCustomValues] = useState({}); // { fieldKey: value }
  const [customFields, setCustomFields] = useState([]); // admin-defined
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  // Phase 4 — breakdown so admins know which part of the total is the
  // typed-in baseline vs auto-summed from MatchPerformance rows.
  const [baseline, setBaseline] = useState(null);
  const [fromMatches, setFromMatches] = useState(null);

  const canEdit = canDo(currentUser, "playerStats", "update");

  // Fetch the stats row + custom field definitions for this (team,
  // player) on mount. Two parallel requests, joined in the .then.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.getPlayerStats(user.id, team.id),
      api.listCricketStatFields()
    ])
      .then(([row, fields]) => {
        if (cancelled) return;
        // Phase 4: form edits the BASELINE only (historical/imported
        // totals). Match-result entries roll up automatically and are
        // shown as a separate "+N from matches" breakdown.
        const b = row.baseline || row;  // fall back to legacy shape
        setStats({
          matchesPlayed: b.matchesPlayed || 0,
          runsScored:    b.runsScored    || 0,
          wicketsTaken:  b.wicketsTaken  || 0,
          catchesTaken:  b.catchesTaken  || 0,
          maidenOvers:   b.maidenOvers   || 0,
          centuries:     b.centuries     || 0,
          sixes:         b.sixes         || 0,
          fours:         b.fours         || 0
        });
        setBaseline(b);
        setFromMatches(row.fromMatches || null);
        // Seed every custom-field key with the saved value (or 0) so the
        // controlled inputs always have a defined value.
        const initialCustom = {};
        for (const f of fields) {
          initialCustom[f.fieldKey] = row.custom?.[f.fieldKey] ?? 0;
        }
        setCustomValues(initialCustom);
        setCustomFields(fields);
        setUpdatedAt(row.updatedAt || null);
        setDirty(false);
      })
      .catch(err => toast.error(err.message || "Couldn't load stats."))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.id, team.id]);

  // Close on Escape so modal feels native.
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function setBuiltIn(key, value) {
    const n = Math.max(0, Math.floor(Number(value) || 0));
    setStats(prev => ({ ...prev, [key]: n }));
    setDirty(true);
  }
  function setCustom(key, value) {
    const n = Math.max(0, Math.floor(Number(value) || 0));
    setCustomValues(prev => ({ ...prev, [key]: n }));
    setDirty(true);
  }

  async function save() {
    if (!canEdit) return;
    setSaving(true);
    try {
      await api.savePlayerStats(user.id, {
        teamId: team.id,
        ...stats,
        custom: customValues
      });
      toast.success(`Stats saved for ${user.name}.`);
      setUpdatedAt(new Date().toISOString());
      setDirty(false);
    } catch (err) {
      toast.error(err.message || "Save failed.");
    } finally { setSaving(false); }
  }

  // Convenience derived numbers — read-only summary chips.
  const battingAvg = stats.matchesPlayed > 0
    ? (stats.runsScored / stats.matchesPlayed).toFixed(1)
    : "—";
  const wicketsPerGame = stats.matchesPlayed > 0
    ? (stats.wicketsTaken / stats.matchesPlayed).toFixed(2)
    : "—";

  return (
    <div className="modal" onClick={onClose}>
      <div className="modalInner playerProfile" onClick={e => e.stopPropagation()}>
        <button className="close" onClick={onClose} title="Close"><X size={20}/></button>

        {/* Header */}
        <header className="pp-head">
          <div className="pp-avatar">
            <UserIcon size={32} />
          </div>
          <div className="pp-headText">
            <small>PLAYER PROFILE · {team.name?.toUpperCase()}</small>
            <h2>{user.name}</h2>
            <div className="pp-roleRow">
              {(user.roles || []).filter(r => r !== "MEMBER").map(r => (
                <span key={r} className={`roleTag roleTag--${r}`}>{r.replace("_", " ")}</span>
              ))}
              {user.email && <span className="pp-email">{user.email}</span>}
            </div>
          </div>
        </header>

        {/* Stats grid */}
        <section className="pp-statsWrap">
          <div className="pp-statsHead">
            <h3>Career Stats — Baseline</h3>
            {updatedAt && (
              <small>Last updated: {new Date(updatedAt).toLocaleDateString()}</small>
            )}
          </div>
          {fromMatches && fromMatches.matchesPlayed > 0 && (
            <div className="pp-breakdown">
              <b>Auto-added from {fromMatches.matchesPlayed} recorded match{fromMatches.matchesPlayed === 1 ? "" : "es"}:</b>
              <span>+{fromMatches.runsScored} runs</span>
              <span>+{fromMatches.wicketsTaken} wkts</span>
              <span>+{fromMatches.centuries} 100s</span>
              <span>+{fromMatches.sixes} 6s</span>
              <span>+{fromMatches.fours} 4s</span>
              <span>+{fromMatches.catchesTaken} catches</span>
            </div>
          )}
          {fromMatches && fromMatches.matchesPlayed === 0 && (
            <small className="pp-breakdown-empty">
              No match results recorded yet. Use the <b>Record Result</b> button on past cricket matches in Schedules to log per-match performance.
            </small>
          )}

          {loading ? (
            <div className="pp-loading"><Loader2 size={18} className="spin"/> Loading stats…</div>
          ) : (
            <>
              <div className="pp-statsGrid">
                {/* Built-in fields first */}
                {BUILTIN_FIELDS.map(f => {
                  const Icon = f.icon;
                  return (
                    <div className="pp-statCard" key={f.key} style={{ "--accent": f.accent }}>
                      <div className="pp-statCard-icon"><Icon size={18}/></div>
                      <div className="pp-statCard-body">
                        <small>{f.label}</small>
                        {canEdit ? (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={stats[f.key]}
                            onChange={e => setBuiltIn(f.key, e.target.value)}
                            className="pp-statInput"
                          />
                        ) : (
                          <b>{stats[f.key]}</b>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Admin-defined custom fields after, in SortOrder. */}
                {customFields.map(f => {
                  const Icon = iconCmp(f.icon);
                  return (
                    <div className="pp-statCard" key={`custom-${f.id}`} style={{ "--accent": f.accent }}>
                      <div className="pp-statCard-icon"><Icon size={18}/></div>
                      <div className="pp-statCard-body">
                        <small>{f.label}</small>
                        {canEdit ? (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={customValues[f.fieldKey] ?? 0}
                            onChange={e => setCustom(f.fieldKey, e.target.value)}
                            className="pp-statInput"
                          />
                        ) : (
                          <b>{customValues[f.fieldKey] ?? 0}</b>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Derived metrics */}
              <div className="pp-derived">
                <div className="pp-derived-item">
                  <small>BATTING AVERAGE</small>
                  <b>{battingAvg}</b>
                  <em>runs / match</em>
                </div>
                <div className="pp-derived-item">
                  <small>WICKETS / MATCH</small>
                  <b>{wicketsPerGame}</b>
                  <em>strike rate</em>
                </div>
              </div>

              {canEdit && (
                <div className="pp-actions">
                  <button
                    className="btn"
                    onClick={save}
                    disabled={!dirty || saving}
                    title={dirty ? "Save stats" : "No changes to save"}
                  >
                    {saving
                      ? <><Loader2 size={14} className="spin"/> Saving…</>
                      : <><Save size={14}/> Save Stats</>}
                  </button>
                  {!dirty && (
                    <small className="pp-actions-hint">
                      Edit any field above to enable the save button.
                    </small>
                  )}
                </div>
              )}
              {!canEdit && (
                <small className="pp-readonly">
                  Read-only view. Only admins and captains can update player stats.
                </small>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
